import React, { useState, useEffect } from "react"
import AvailabilityRulesSelect from "../AvailabilityRulesSelect"
import currencyCodesData from "./currencyCodesData.json"
import FlexPaymentDetails from "../FlexPaymentsDetails"
import InstallmentDetails from "./InstallmentDetails"
import Select from "react-select"
import SyncPrompt from "../SyncPrompt"
import { ServiceType } from "../hooks/useManageServices"
import { Button } from "../../ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu"
import {
  installmentIncrementOptions,
  timeTypes,
  packageBillingTypes,
} from "../../../objects/createServiceObjects"
import { Label } from "../../ui/label"
import { SvgTrash, SvgWarning } from "../../icons"
import { Switch } from "../../ui/switch"

interface CurrencyCodes {
  code: string
  symbol: string
}

interface Props {
  formErrors: any
  serviceValues: any
  setServiceValues: any
  serviceType: ServiceType
  isGroupService: boolean
  availabilities: Array<any>
  handleAvailabilityRulesChange: any
  service: any
  paypalStatus: any
}

const PackageForm: React.FC<Props> = ({
  formErrors,
  serviceValues,
  setServiceValues,
  serviceType,
  isGroupService,
  availabilities,
  handleAvailabilityRulesChange,
  service,
  paypalStatus,
}) => {
  const [showSyncPrompt, setShowSyncPrompt] = useState(false)
  const [currencyCodes, setCurrencyCodes] = useState<CurrencyCodes[]>([])

  const isLocked =
    (service?.enrolled_count ?? 0) >= 1 &&
    String(service?.is_group_service) === "true"

  const showSessionCost = () => {
    let showCost = false
    if (!serviceValues?.isFree) {
      showCost = true
    } else {
      showCost = false
    }
    return showCost
  }

  useEffect(() => {
    if (serviceValues.group.isFree && serviceValues.isGroupService) {
      setServiceValues({
        ...serviceValues,
        package: { ...serviceValues.package, packageCost: 0.0 },
      })
    }
  }, [serviceValues.group])

  useEffect(() => {
    if (serviceValues.isFree && serviceValues.package.packageCost !== 0) {
      setServiceValues((prev: any) => ({
        ...prev,
        package: { ...prev.package, packageCost: 0 },
      }))
    }
  }, [serviceValues.isFree])

  const getFirstInstallmentAmount = () => {
    const upFrontPercentage = serviceValues?.package?.upFrontPercentage / 100
    return (serviceValues?.package?.packageCost * upFrontPercentage).toFixed(2)
  }

  const getSubsequentInstallmentAmount = () => {
    const firstInstallmentAmount = parseFloat(getFirstInstallmentAmount())
    const numberInstallments =
      serviceValues?.package?.installmentIncrements?.length
    const remainingAmount =
      parseFloat(serviceValues?.package?.packageCost) -
      parseFloat(firstInstallmentAmount.toFixed(2))
    const subsequentInstallmentAmount = (
      remainingAmount / numberInstallments
    ).toFixed(2)
    return subsequentInstallmentAmount
  }

  const billingTypeToPaymentTerm = (billingType: string) => {
    switch (billingType) {
      case "bulk":
        return "full_pay"
      case "installment":
        return "installment"
      case "flexible":
        return "flexible"
      default:
        return "installment"
    }
  }

  useEffect(() => {
    const expectedPaymentTerm = billingTypeToPaymentTerm(
      serviceValues.package.packageBillingType
    )

    if (serviceValues.payment_term !== expectedPaymentTerm) {
      setServiceValues((prev: any) => ({
        ...prev,
        payment_term: expectedPaymentTerm,
      }))
    }
  }, [serviceValues.package.packageBillingType])

  useEffect(() => {
    if (serviceValues?.package?.installmentIncrements?.length === 0) {
      setServiceValues({
        ...serviceValues,
        package: {
          ...serviceValues.package,
          upFrontPercentage: 100,
        },
      })
    }
  }, [serviceValues?.package?.installmentIncrements?.length])

  useEffect(() => {
    if (serviceValues?.package?.upFrontPercentage === 100) {
      setServiceValues({
        ...serviceValues,
        package: {
          ...serviceValues.package,
          installmentIncrements: [],
        },
      })
    }
  }, [serviceValues?.package?.upFrontPercentage])

  useEffect(() => {
    const packageCostInput = document.getElementById("packageCostInput") || null
    const percentDueInput = document.getElementById("percentDue") || null
    if (packageCostInput) {
      packageCostInput.addEventListener("keydown", function (e) {
        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
          e.preventDefault()
        }
      })
    }
    if (percentDueInput) {
      percentDueInput.addEventListener("keydown", function (e) {
        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
          e.preventDefault()
        }
      })
    }
  }, [])

  useEffect(() => {
    const data: CurrencyCodes[] = currencyCodesData.codes
    setCurrencyCodes(data)
  }, [])

  const handleCurrencySelect = (code: CurrencyCodes) => {
    setServiceValues({
      ...serviceValues,
      currency: code.code,
      symbol: code.symbol,
    })
  }

  const checkStatus = () => {
    if (!paypalStatus?.data.onboarding_completed) {
      setShowSyncPrompt(true)
      return false
    }

    return true
  }

  return (
    <>
      <h4 className="font-bold text-base mb-[16px]">
        How many sessions in this package?
      </h4>
      <div
        className={`default-input mb-[24px] w-full ${
          formErrors?.packageSessions && "input-error"
        }`}
      >
        <input
          type="number"
          min="0"
          onWheel={() => (document.activeElement as HTMLElement)?.blur()}
          value={serviceValues?.package?.packageSessions}
          onChange={(e: any) => {
            setServiceValues({
              ...serviceValues,
              package: {
                ...serviceValues.package,
                packageSessions: e.target.value ? parseInt(e.target.value) : "",
              },
            })
          }}
          name="packageSessions"
          disabled={isLocked}
        />
        <div className="overlay">Sessions</div>
      </div>
      {formErrors.packageSessions && (
        <div className="field-error w-full">
          <SvgWarning />
          {formErrors.packageSessions}
        </div>
      )}

      <h4 className="font-bold text-base mb-[16px]">
        What is the length of each session?
      </h4>
      <div
        className={`${
          formErrors?.sessionLength && "input-error"
        } flex items-center gap-[12px] mb-[24px]`}
      >
        <input
          type="number"
          onWheel={() => (document.activeElement as HTMLElement)?.blur()}
          min="0"
          max="60"
          className="w-[54px] h-[54px] bg-white focus:outline-0 border
                    border-1 border-grayCloud rounded-[10px] text-graySlate text-center"
          value={serviceValues.sessionLength.minutes}
          name="sessionMinutes"
          onChange={(e: any) =>
            setServiceValues({
              ...serviceValues,
              sessionLength: { minutes: e.target.value },
            })
          }
          disabled={isLocked}
        />
        <span className="font-bold text-base">minutes</span>
      </div>
      {formErrors.sessionLength && (
        <div className="field-error w-[100%]">
          <SvgWarning />
          {formErrors.sessionLength}
        </div>
      )}

      {/* SELECT WITH EXISTING AVAILABILITY RULES */}
      {availabilities?.length > 0 && (
        <AvailabilityRulesSelect
          availabilities={availabilities}
          serviceType={serviceType}
          serviceValues={serviceValues}
          handleAvailabilityRulesChange={handleAvailabilityRulesChange}
          service={service}
        />
      )}

      <div className="w-full h-px bg-grayMist my-[32px]"></div>

      {/* GROUP IS FREE TOGGLE */}
      {isGroupService && (
        <>
          <div className="flex flex-col mb-[20px]">
            <h4
              className={`font-bold text-base mb-[8px] ${
                service.enrolled_count >= 1 ? "text-graySlate" : ""
              }`}
            >
              Is this a free group?
            </h4>
            <div className="flex items-center gap-2">
              <Switch
                onCheckedChange={() => {
                  if (!checkStatus()) return
                  setServiceValues({
                    ...serviceValues,
                    isFree: !serviceValues.isFree,
                    group: {
                      ...serviceValues.group,
                      isFree: !serviceValues.group.isFree,
                    },
                  })
                }}
                checked={
                  serviceValues.group.isFree && serviceValues.group.isFree
                }
                id="free"
                disabled={isLocked}
              />

              <Label htmlFor="free">Free</Label>
            </div>
          </div>
        </>
      )}
      {!isGroupService && (
        <div className="flex flex-col mb-[20px]">
          <h4
            className={`font-bold text-base mb-[8px] ${
              service.enrolled_count >= 1 ? "text-graySlate" : ""
            }`}
          >
            Is this a free service?
          </h4>
          <div className="flex items-center gap-2">
            <Switch
              onCheckedChange={() => {
                if (!checkStatus()) return

                setServiceValues({
                  ...serviceValues,
                  isFree: !serviceValues.isFree,
                })
              }}
              checked={serviceValues.isFree}
              id="free"
            />
            <Label htmlFor="free">Free</Label>
          </div>
        </div>
      )}

      {showSessionCost() && (
        <>
          <div className="flex flex-col mb-4">
            <h4 className="font-bold text-base mb-[8px]">
              How do you want to bill the client?
            </h4>
            <Select
              className="basic-single w-full"
              classNamePrefix="select"
              options={packageBillingTypes}
              value={
                packageBillingTypes.find(
                  (option) =>
                    option.value === serviceValues.package.packageBillingType
                ) ?? null
              }
              menuPlacement="bottom"
              name="packageBillingType"
              onChange={(e: any) => {
                const newBillingType = e.value
                const newPaymentTerm = billingTypeToPaymentTerm(newBillingType)

                setServiceValues({
                  ...serviceValues,
                  payment_term: newPaymentTerm,
                  allowPromo: newBillingType === "bulk",
                  package: {
                    ...serviceValues.package,
                    packageBillingType: newBillingType,
                  },
                })
              }}
              isDisabled={isLocked}
            />
          </div>
          <h4
            className={`font-bold text-base mb-[8px] ${
              service.enrolled_count >= 1 ? "text-graySlate" : ""
            }`}
          >
            Package price
          </h4>
          <fieldset
            disabled={isLocked}
            className={`relative w-full ${
              isLocked ? "opacity-50 pointer-events-none" : ""
            }`}
          >
            <div
              className={`relative ${
                formErrors?.packageCost && "input-error"
              } default-input w-full`}
            >
              <div className="flex">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="icon"
                      variant="utility"
                      disabled={isLocked}
                      onClick={(e) => {
                        if (isLocked) e.preventDefault()
                      }}
                    >
                      {serviceValues.currency}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel className="text-graySlate text-xs p-2">
                      Currency
                    </DropdownMenuLabel>
                    <DropdownMenuGroup>
                      {currencyCodes.map((code) => (
                        <DropdownMenuItem
                          key={code.code}
                          onClick={() =>
                            !isLocked && handleCurrencySelect(code)
                          }
                        >
                          {code.code}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
                <input
                  id="packageCostInput"
                  type="number"
                  onWheel={() =>
                    (document.activeElement as HTMLElement)?.blur()
                  }
                  step="0.01"
                  min="0"
                  autoComplete="off"
                  name="packageCost"
                  placeholder="Cost"
                  value={serviceValues.package.packageCost}
                  onChange={(e: any) =>
                    setServiceValues({
                      ...serviceValues,
                      package: {
                        ...serviceValues.package,
                        packageCost: e.target.value,
                      },
                    })
                  }
                  className="ml-2 flex-1 peer border border-gray-300 py-2 focus:outline-none focus:border-blue-500"
                  disabled={isLocked}
                />
              </div>

              {formErrors.packageCost && (
                <div className="field-error w-full">
                  <SvgWarning />
                  {formErrors.packageCost}
                </div>
              )}
            </div>
          </fieldset>
          <br />
          <div className="flex flex-col">
            <h4 className="font-bold text-base mb-[8px]">
              Enable clients to use your promo codes?
            </h4>
            <div className="flex items-center gap-2">
              <Switch
                onCheckedChange={() =>
                  setServiceValues({
                    ...serviceValues,
                    allowPromo: !serviceValues.allowPromo,
                  })
                }
                checked={serviceValues.allowPromo}
                id="promo"
                disabled={isLocked}
              />
              <Label htmlFor="free">Enabled</Label>
            </div>
          </div>

          {serviceValues.payment_term === "full_pay" && (
            <div className="w-full h-px bg-grayMist my-[32px]"></div>
          )}

          {serviceType === "bundle" &&
            (serviceValues.payment_term === "full_pay" ? (
              <>
                {/* <div className="w-full h-px bg-grayMist my-[32px]"></div> */}
                <h4 className="font-bold text-base mb-[8px]">Preview</h4>
                <div className="w-full border rounded-xl p-[20px] flex flex-col">
                  <p className="text-graySlate text-base mb-[32px]">
                    Full Payment Schedule
                  </p>
                  <div className="flex w-full items-center justify-between mb-[16px]">
                    <div className="flex items-center gap-[16px] min-w-fit">
                      <div className="w-[24px] h-[24px] bg-primaryBlue rounded-full text-white font-bold justify-center items-center flex">
                        {serviceValues.symbol &&
                          String.fromCharCode(
                            parseInt(serviceValues.symbol.slice(2), 16)
                          )}
                      </div>
                      <p className="text-[16px]">Payment at checkout</p>
                    </div>
                    <div className="border-grayCloud mx-[16px] h-2 w-[30%] -translate-y-1 transform border-b border-dashed"></div>

                    <span className="text-base">
                      {serviceValues.symbol &&
                        String.fromCharCode(
                          parseInt(serviceValues.symbol.slice(2), 16)
                        )}
                      {`${parseInt(serviceValues?.package?.packageCost).toFixed(
                        2
                      )}`}
                    </span>
                  </div>
                  <div className="text-graySlate w-full bg-grayMist rounded-lg p-[26px] flex items-center justify-center text-base">
                    <p className="text-center">
                      Payment will be charged{" "}
                      <strong className="text-black">immediately</strong> at
                      checkout.
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <>
                {showSessionCost() &&
                  (serviceValues.payment_term === "flexible" ||
                    serviceValues.payment_term === "installment") && (
                    <InstallmentDetails
                      formErrors={formErrors}
                      isLocked={isLocked}
                      serviceValues={serviceValues}
                      setServiceValues={setServiceValues}
                    />
                  )}
                <FlexPaymentDetails
                  serviceValues={serviceValues}
                  serviceType={serviceType}
                />
              </>
            ))}
        </>
      )}

      {showSyncPrompt && (
        <SyncPrompt setSyncPrompt={setShowSyncPrompt} service={service} />
      )}
    </>
  )
}

export default PackageForm
